import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { FileDown, Clock, CheckCircle2, XCircle, Loader2, MessageSquareWarning, ExternalLink, Lock, Zap } from "lucide-react";
import { redirectIfPhonePe } from "../lib/payRail";
import { PhonePeBadge } from "./payments/RailSelector";
import { DualPayLinks } from "./payments/DualPayLinks";

/**
 * Buyer · "My Quotations" card.
 *
 * Renders every non-draft quotation on `/buyer-quotations/mine`, with:
 *   • live 48h countdown timer for sent quotations
 *   • View PDF button
 *   • Accept & Pay button — pops Razorpay checkout inline
 *   • Request Revision button — kicks the quotation back to admin with a
 *     buyer note (flips it to `revision_requested`)
 *
 * Payment success is wired to the existing `/orders/{oid}/verify` endpoint
 * so we don't duplicate the checkout logic that lives in Checkout.jsx.
 */

const STATUS_COLOUR = {
  sent: "#D8B23E",
  accepted: "#2FB999",
  expired: "#8A8A82",
  cancelled: "#FF5A5F",
  revision_requested: "#B94FEB",
};

function formatCountdown(ms) {
  if (ms <= 0) return "expired";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function PriceLockHero({ deadline, bookingAmount, quotationNumber, payUrl, payUrls }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!deadline) return null;
  const left = new Date(deadline).getTime() - now;
  const expired = left <= 0;
  if (expired) return null;
  const urgent = left < 30 * 60 * 1000;
  const bg = urgent
    ? "linear-gradient(135deg, #1B0A0A 0%, #2A0D0D 100%)"
    : "linear-gradient(135deg, #1B1401 0%, #2A1F02 100%)";
  const accent = urgent ? "#C0392B" : "#D8B23E";
  return (
    <div data-testid="price-lock-hero"
         className="rounded-sm border p-5 md:p-6"
         style={{ background: bg, borderColor: accent + "55" }}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em]"
               style={{ color: accent }}>
            <Lock size={11} /> Vinci Price Lock · Booking window
          </div>
          <div className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-2">
            ⏳ <span className="font-mono" data-testid="price-lock-hero-timer">
              {formatCountdown(left)}
            </span> left to lock your rate
          </div>
          <div className="font-body text-sm text-[#C9C9C2] mt-2 leading-relaxed max-w-xl">
            Pay the <b style={{ color: accent }}>₹{Number(bookingAmount || 0).toLocaleString("en-IN")} (30%)</b> booking
            amount on <b>{quotationNumber}</b> to freeze today's daily rate.
            {urgent && (
              <span className="block mt-1 text-[#C0392B] font-grotesk text-xs uppercase tracking-[0.16em]">
                ⚡ Under 30 minutes remaining
              </span>
            )}
          </div>
        </div>
        {(payUrl || payUrls?.phonepe) && (
          <div className="shrink-0 self-center" data-testid="price-lock-hero-pay">
            <DualPayLinks testId="price-lock-hero" size="sm" urls={{ razorpay: payUrls?.razorpay || payUrl, phonepe: payUrls?.phonepe }} />
          </div>
        )}
      </div>
    </div>
  );
}

function CountdownPill({ validUntil }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!validUntil) return null;
  const left = new Date(validUntil).getTime() - now;
  const expired = left <= 0;
  return (
    <span data-testid="bq-countdown"
          className={`font-mono text-xs px-2 py-0.5 rounded-sm inline-flex items-center gap-1 border ${expired ? "border-[#FF5A5F]/40 text-[#FF5A5F]" : "border-[#D8B23E]/40 text-[#D8B23E]"}`}>
      <Clock size={11} /> {expired ? "Expired" : `${formatCountdown(left)} left`}
    </span>
  );
}

export function MyBuyerQuotations({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [revisionForId, setRevisionForId] = useState(null);
  const [revisionNote, setRevisionNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/buyer-quotations/mine")).data || []); }
    catch { /* silent — buyer may have zero quotations */ }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const backend = process.env.REACT_APP_BACKEND_URL;

  const acceptAndPay = async (q, railChoice = "razorpay") => {
    setBusyId(q.id);
    try {
      const { data } = await api.post(`/buyer-quotations/${q.id}/accept`);
      const { order, key_id, payment_enabled } = data;
      if (!payment_enabled) {
        toast.success("Quotation accepted — our accounts team will follow up with payment instructions.");
        await load();
        return;
      }
      if (railChoice === "phonepe") {
        const pp = await api.post(`/orders/${order.id}/pay/phonepe`);
        if (redirectIfPhonePe(pp.data)) return;
        throw new Error("PhonePe could not open — please choose Pay via Razorpay.");
      }
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error("Could not load Razorpay checkout"); return; }
      const rzp = new window.Razorpay({
        key: key_id,
        amount: Math.round(order.total * 100),
        currency: "INR",
        name: "Vinci Inc.",
        description: `Order for ${q.quotation_number}`,
        order_id: order.razorpay_order_id,
        prefill: { email: order.buyer_email, contact: order.shipping_phone || "" },
        theme: { color: "#B87333" },
        handler: async (resp) => {
          try {
            await api.post(`/orders/${order.id}/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment received — tax invoice on its way to your inbox.");
            await load();
          } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
        },
        modal: { ondismiss: () => setBusyId(null) },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Accept failed");
    } finally { setBusyId(null); }
  };

  const submitRevision = async (id) => {
    if (!revisionNote.trim()) { toast.error("Add a short note so admin knows what to change"); return; }
    setBusyId(id);
    try {
      await api.post(`/buyer-quotations/${id}/request-revision`, { note: revisionNote.trim() });
      toast.success("Revision requested — admin will follow up soon.");
      setRevisionForId(null); setRevisionNote("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit revision"); }
    finally { setBusyId(null); }
  };

  const grouped = useMemo(() => {
    const g = { sent: [], revision_requested: [], accepted: [], expired: [], cancelled: [] };
    for (const q of items) (g[q.status] || (g[q.status] = [])).push(q);
    return g;
  }, [items]);

  if (loading) return null;
  if (items.length === 0) return null;   // hide the section entirely if there's nothing

  return (
    <section data-testid="my-buyer-quotations" className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl md:text-3xl">Your quotations <span className="text-[#8A8A82] text-lg">({items.length})</span></h2>
        <button type="button" onClick={load} className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] hover:text-[#B87333]">Refresh</button>
      </div>

      {["sent", "revision_requested", "accepted", "expired", "cancelled"].map((lane) => {
        const list = grouped[lane] || [];
        if (list.length === 0) return null;
        return (
          <div key={lane} className="space-y-3">
            {list.map((q) => {
              const colour = STATUS_COLOUR[q.status] || "#8A8A82";
              const total = Number(q.total || 0);
              return (
                <motion.div key={q.id} data-testid={`my-quotation-${q.id}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    className="border border-white/10 rounded-sm bg-[#0a0a0a] p-5 space-y-4">
                  {q.status === "sent" && q.price_lock_stage === "booking_sent" && q.booking_deadline && (
                    <PriceLockHero deadline={q.booking_deadline}
                                   bookingAmount={q.booking_amount}
                                   quotationNumber={q.quotation_number}
                                   payUrl={q.booking_payment_link_url} payUrls={q.booking_pay_urls} />
                  )}
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-grotesk text-[10px] uppercase tracking-[0.22em]" style={{ color: colour }}>
                          {q.status.replace("_", " ")}
                        </span>
                        <span className="font-serif text-xl text-[#F5F5F0]">{q.quotation_number}</span>
                        {q.status === "sent" && <CountdownPill validUntil={q.valid_until} />}
                      </div>
                      <div className="font-body text-sm text-[#8A8A82] mt-1">
                        ₹{total.toLocaleString("en-IN")} · {q.items?.length || 0} line item(s){q.rfq_ref ? ` · RFQ ${q.rfq_ref}` : ""}
                      </div>
                      {q.status === "accepted" && q.order_id && (
                        <div className="font-body text-xs text-[#2FB999] mt-2 inline-flex items-center gap-1">
                          <CheckCircle2 size={12}/> Order created — check "Your orders" below
                        </div>
                      )}
                      {q.status === "revision_requested" && q.revision_note && (
                        <div className="font-body text-xs text-[#B94FEB] mt-2 italic border-l-2 border-[#B94FEB]/40 pl-2">
                          Revision requested: “{q.revision_note}”
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <a href={`${backend}/api/buyer-quotations/${q.id}/download.pdf`}
                         target="_blank" rel="noreferrer"
                         data-testid={`my-quotation-pdf-${q.id}`}
                         className="border border-white/20 hover:border-[#B87333] font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-sm text-[#C9C9C2] hover:text-[#B87333] inline-flex items-center gap-1.5">
                        <FileDown size={11}/> View PDF
                      </a>
                      {q.status === "sent" && (
                        <>
                          <div className="flex flex-wrap gap-2" data-testid={`my-quotation-accept-${q.id}`}>
                            <button type="button" onClick={() => acceptAndPay(q, "razorpay")} disabled={busyId === q.id}
                                    data-testid={`my-quotation-accept-razorpay-${q.id}`}
                                    className="bg-[#D8B23E] hover:bg-[#E5C450] text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2 rounded-sm inline-flex items-center gap-1.5 disabled:opacity-50">
                              {busyId === q.id ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle2 size={12}/>}
                              Accept &amp; Pay via Razorpay
                            </button>
                            <button type="button" onClick={() => acceptAndPay(q, "phonepe")} disabled={busyId === q.id}
                                    data-testid={`my-quotation-accept-phonepe-${q.id}`}
                                    className="bg-[#5F259F] hover:bg-[#6E2FB8] text-white font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2 rounded-sm inline-flex items-center gap-1.5 disabled:opacity-50">
                              {busyId === q.id ? <Loader2 size={12} className="animate-spin"/> : <PhonePeBadge size={12} />}
                              Accept &amp; Pay via PhonePe
                            </button>
                          </div>
                          <button type="button" onClick={() => setRevisionForId(revisionForId === q.id ? null : q.id)}
                                  data-testid={`my-quotation-revise-${q.id}`}
                                  className="border border-[#B94FEB]/50 text-[#B94FEB] hover:bg-[#B94FEB]/10 font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
                            <MessageSquareWarning size={11}/> Request revision
                          </button>
                        </>
                      )}
                      {q.status === "accepted" && q.order_id && (
                        <a href="#your-orders" className="border border-[#2FB999]/40 text-[#2FB999] font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
                          View order <ExternalLink size={10}/>
                        </a>
                      )}
                    </div>
                  </div>
                  {revisionForId === q.id && (
                    <div className="mt-3 border-t border-white/10 pt-3">
                      <textarea data-testid={`my-quotation-revise-note-${q.id}`} value={revisionNote} onChange={(e) => setRevisionNote(e.target.value)}
                                placeholder="What should we change? (e.g. lower the unit rate on cement, add binding wire, change delivery state)"
                                rows={2}
                                className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B94FEB]" />
                      <div className="mt-2 flex gap-2">
                        <button type="button" onClick={() => submitRevision(q.id)} disabled={busyId === q.id}
                                data-testid={`my-quotation-revise-submit-${q.id}`}
                                className="bg-[#B94FEB] hover:bg-[#A139D9] text-black font-grotesk text-[10px] uppercase tracking-[0.22em] px-4 py-1.5 rounded-sm">
                          {busyId === q.id ? "Sending…" : "Submit"}
                        </button>
                        <button type="button" onClick={() => { setRevisionForId(null); setRevisionNote(""); }}
                                className="text-[#8A8A82] font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-1.5"><XCircle size={11} className="inline mr-1"/> Cancel</button>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        );
      })}
    </section>
  );
}
